// usePins — shared PINNED state for the AppShell (Cat-2 nav). ONE fetch per scope, shared by the
// left-nav PINNED section + the pin/unpin affordance in the Context Rail, so a pin created in one
// place reflects everywhere. Reads through useScopeData (the scope chokepoint) so the ethical wall
// (inherit-down) holds — the list is filtered SERVER-SIDE to pins visible from the reader's scope;
// the per-scope cap (5) + owner isolation are enforced server-side (workspace_backend/app/api/v1/pins.py).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useScopeData } from "./useScopeData";

export type PinTargetType = "source" | "context_graph" | "scope_node";

/** Mirror of workspace_backend/app/api/v1/pins.py::PinResponse. */
export interface PinItem {
  id: string;
  scope_node_id: string;
  scope_path: string;
  target_type: PinTargetType;
  target_id: string;
  label: string | null;
}

export interface PinCreate {
  scope_node_id: string;
  target_type: PinTargetType;
  target_id: string;
  label?: string | null;
}

/** Per-scope cap mirrored from the server (PINNED_CAP) for an optimistic client-side hint. */
export const PIN_CAP = 5;

export interface PinsState {
  pins: PinItem[];
  /** True once a scope has resolved (a walled /pins read is possible). */
  ready: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Add a pin. Resolves on success; REJECTS with the server message on cap/validation failure. */
  addPin: (p: PinCreate) => Promise<void>;
  /** Remove a pin by id (owner-only, server-enforced). */
  removePin: (id: string) => Promise<void>;
  /** The matching pin if (targetType,targetId) is already pinned in the current view, else undefined. */
  pinOf: (targetType: PinTargetType, targetId: string) => PinItem | undefined;
}

const PinsContext = createContext<PinsState | null>(null);

export function PinsProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { ready, fetchScoped } = useScopeData();
  const [pins, setPins] = useState<PinItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!ready) {
      setPins([]);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchScoped<PinItem[]>("/pins");
      setPins(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ready, fetchScoped]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const addPin = useCallback(
    async (p: PinCreate) => {
      // Let the caller surface cap/validation errors (the server returns 409 PIN_CAP_REACHED /
      // 422 INVALID_TARGET_TYPE) — we re-throw rather than swallow so the UI can toast honestly.
      await fetchScoped<PinItem>("/pins", {
        init: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(p),
        },
      });
      await refresh();
    },
    [fetchScoped, refresh],
  );

  const removePin = useCallback(
    async (id: string) => {
      await fetchScoped(`/pins/${id}`, { init: { method: "DELETE" } });
      await refresh();
    },
    [fetchScoped, refresh],
  );

  const pinOf = useCallback(
    (targetType: PinTargetType, targetId: string) =>
      pins.find((p) => p.target_type === targetType && p.target_id === targetId),
    [pins],
  );

  const value = useMemo<PinsState>(
    () => ({ pins, ready, loading, error, refresh, addPin, removePin, pinOf }),
    [pins, ready, loading, error, refresh, addPin, removePin, pinOf],
  );

  return <PinsContext.Provider value={value}>{children}</PinsContext.Provider>;
}

export function usePins(): PinsState {
  const ctx = useContext(PinsContext);
  if (!ctx) throw new Error("usePins must be used within <PinsProvider>");
  return ctx;
}
